import { useEffect, useState } from "react";
import Footer from "@/components/sections/Footer";

// Account & data deletion — canonical hosted page for FolioStax. This is the URL entered in the
// Google Play Console (App content → Data deletion): https://fyzeron.com/foliostax/delete-account
//
// A user who cannot open the app must still be able to request deletion here, so the form posts an
// UNAUTHENTICATED request to the backend, which queues it for verified processing (it never
// auto-deletes — identity can't be proven from an open form). If the API can't be reached from the
// browser, we fall back to a pre-filled email so the request always gets through.
//
// Logged-in users can also delete instantly from inside the app: Settings → My Account → Delete
// account (that path is authenticated and executes immediately).

// HTTPS API base for the browser to call. Override with REACT_APP_BACKEND_URL at build time.
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "https://api.fyzeron.com").replace(/\/$/, "");
const DELETE_REQUEST_URL = `${API_BASE}/api/v1/account/deletion-request`;
const SUPPORT_EMAIL = "info@fyzeron.com";

const WHAT_HAPPENS = [
  {
    title: "What gets deleted",
    body:
      "Your account and login, your business/organisation profile and branches, and the data you " +
      "entered — items, inventory, customers and suppliers, invoices and transactions, reports, " +
      "print formats, settings, and any cloud-synced copies on our servers.",
  },
  {
    title: "What may be retained",
    body:
      "Because FolioStax is an accounting tool, records that we are legally required to keep — such " +
      "as transaction and tax records — may be retained for the period required by applicable law, " +
      "after which they are deleted. Retained records are kept only for legal compliance and are not " +
      "used for any other purpose. Anonymous, aggregated statistics that cannot identify you may also " +
      "be kept.",
  },
  {
    title: "How long it takes",
    body:
      "Requests submitted here are verified and processed within 30 days. Deleting from inside the " +
      "app (Settings → My Account → Delete account) takes effect immediately.",
  },
];

export default function DeleteAccount() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", reason: "" });
  const [state, setState] = useState("idle"); // idle | submitting | done | fallback
  const [error, setError] = useState("");

  useEffect(() => {
    const prev = document.title;
    document.title = "FolioStax — Delete Your Account";
    window.scrollTo(0, 0);
    return () => {
      document.title = prev;
    };
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const mailtoFallback = () => {
    const subject = encodeURIComponent("FolioStax account deletion request");
    const body = encodeURIComponent(
      `I would like to delete my FolioStax account and associated data.\n\n` +
        `Name: ${form.name || "-"}\n` +
        `Registered mobile: ${form.mobile || "-"}\n` +
        `Email: ${form.email || "-"}\n` +
        `Reason (optional): ${form.reason || "-"}\n`
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.mobile.trim()) {
      setError("Please enter the mobile number registered with your account.");
      return;
    }
    setState("submitting");
    try {
      const res = await fetch(DELETE_REQUEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim() || null,
          mobile: form.mobile.trim(),
          email: form.email.trim() || null,
          reason: form.reason.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setState("done");
    } catch (err) {
      // API unreachable from the browser (offline, CORS, or not yet on HTTPS) — fall back to email
      // so the request still reaches us.
      setState("fallback");
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center" aria-label="Fyzeron Technologies LLP — home">
            <img src="/fyzeron-logo-dark.png" alt="Fyzeron Technologies LLP" className="h-9 w-auto" />
          </a>
          <a href="/" className="text-sm font-semibold text-slate-600 hover:text-navy transition-colors">
            ← Back to home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800">Privacy</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy mt-3">
            Delete your account
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            You can permanently delete your FolioStax account and the data associated with it. If you
            can still sign in, the quickest way is inside the app:{" "}
            <span className="font-semibold text-slate-900">
              Settings → My Account → Delete account
            </span>
            . Otherwise, use the form below and we&rsquo;ll process your request.
          </p>

          {/* What happens */}
          <div className="mt-10 space-y-8">
            {WHAT_HAPPENS.map((s) => (
              <section key={s.title}>
                <h2 className="font-display font-bold text-xl text-slate-900">{s.title}</h2>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.body}</p>
              </section>
            ))}
          </div>

          {/* Request form */}
          <section className="mt-12">
            <h2 className="font-display font-bold text-2xl text-slate-900">Request deletion</h2>

            {state === "done" ? (
              <div
                data-testid="deletion-success"
                className="mt-5 rounded-2xl border border-green-200 bg-green-50 p-6 text-green-900"
              >
                <p className="font-semibold">Your request has been received.</p>
                <p className="mt-2 text-sm leading-relaxed">
                  We&rsquo;ll verify the account and delete it within 30 days. You may be contacted at
                  the details you provided to confirm ownership.
                </p>
              </div>
            ) : state === "fallback" ? (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
                <p className="font-semibold">We couldn&rsquo;t submit the form automatically.</p>
                <p className="mt-2 text-sm leading-relaxed">
                  Please send your request by email instead — we&rsquo;ve pre-filled it for you.
                </p>
                <a
                  href={mailtoFallback()}
                  className="mt-4 inline-flex rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-blue-900 transition"
                >
                  Email your request
                </a>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-5 space-y-5" data-testid="deletion-form">
                <Field label="Name">
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="fs-input"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </Field>
                <Field label="Registered mobile number" required>
                  <input
                    type="tel"
                    value={form.mobile}
                    onChange={update("mobile")}
                    className="fs-input"
                    placeholder="e.g. +91 98765 43210"
                    autoComplete="tel"
                    required
                  />
                </Field>
                <Field label="Email (optional)">
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="fs-input"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </Field>
                <Field label="Reason (optional)">
                  <textarea
                    value={form.reason}
                    onChange={update("reason")}
                    className="fs-input min-h-[96px] resize-y"
                    placeholder="Tell us why you're leaving (optional)"
                  />
                </Field>

                {error ? <p className="text-sm font-medium text-red-600">{error}</p> : null}

                <button
                  type="submit"
                  disabled={state === "submitting"}
                  data-testid="deletion-submit"
                  className="inline-flex items-center justify-center rounded-full bg-red-600 px-8 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 transition"
                >
                  {state === "submitting" ? "Submitting…" : "Request account deletion"}
                </button>
                <p className="text-xs text-slate-400">
                  Prefer email? Write to{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-800 hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </form>
            )}
          </section>

          <p className="mt-14 pt-8 border-t border-slate-200 text-sm text-slate-400">
            © {new Date().getFullYear()} Fyzeron Technologies LLP. All rights reserved.
          </p>
        </div>
      </main>

      {/* Local input styling (kept inline so the page is self-contained). */}
      <style>{`
        .fs-input {
          width: 100%;
          border: 1px solid rgb(226 232 240);
          border-radius: 0.75rem;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          background: #fff;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .fs-input:focus {
          border-color: rgb(30 64 175);
          box-shadow: 0 0 0 3px rgba(30,64,175,0.12);
        }
      `}</style>

      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-800">
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
